import {
  Avatar,
  BoundMultiSelectField,
  BoundNumberField,
  BoundSelectField,
  BoundTextAreaField,
  BoundTreeSelectField,
  Button,
  Css,
  PresentationProvider,
  StaticField,
  TextField,
  useComputed,
  useModal,
  useSuperDrawer,
} from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import pick from "lodash/pick";
import { Observer } from "mobx-react";
import { useCallback, useMemo } from "react";
import {
  BoundAttachments,
  SaveAttachmentModel,
  attachmentConfig,
} from "src/components/boundAttachments/BoundAttachments";
import { ChangeLogRightPaneContentDetailsQuery } from "src/components/change-log/ChangeLogRightPaneContent.generated";
import {
  CreateChangeRequestDrawer,
  getAssetTreeSelectOptions,
  getProjectTreeSelectValues,
  getRequiredChangeSourceAssets,
} from "src/components/change-log/CreateChangeRequestDrawer";
import { useSaveChangeRequestMutation } from "src/components/change-log/CreateChangeRequestDrawer.generated";
import {
  AssetTreeSelectValuesFragment,
  ChangeRequestDetailsFragment,
  ProjectTreeSelectValuesFragment,
} from "src/fragments/circularFragments.generated";
import {
  ChangeRequestDependency,
  ChangeRequestPriority,
  ChangeRequestSource,
  ChangeRequestStatus,
  ChangeType,
  IncrementalCollectionOp,
  InputMaybe,
  SaveChangeRequestAsset,
  SaveChangeRequestInput,
} from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { cannotBeEmpty, daysAgo, isDefined, mapFormInputToIncrementalOp, maybeDeTagId, noop } from "src/utils";
import { getProjectsAndDevelopmentMenuLabel } from "src/utils/projects";
import { isPersonalizationChangeSource } from "../utils";
import { CustomerRequestPersonalizationInputs } from "./CustomerRequestPersonalizationInputs";
import { DependencyModal } from "./DependencyModal";
import { UrgencyField } from "./UrgencyField";

type DetailsTabProps = {
  changeRequest: ChangeRequestDetailsFragment;
  selectValues: {
    enums: ChangeLogRightPaneContentDetailsQuery["enumDetails"];
    regions: ProjectTreeSelectValuesFragment[];
    assets: AssetTreeSelectValuesFragment[];
  };
};

export const READONLY_STATUSES = [
  ChangeRequestStatus.Approved,
  ChangeRequestStatus.Denied,
  ChangeRequestStatus.Completed,
];

const ALLOWED_SOURCES = [
  ChangeRequestSource.CustomerRequestPersonalization,
  ChangeRequestSource.BoylCustomerRequestPersonalization,
  ChangeRequestSource.InternalSalesFeedback,
  ChangeRequestSource.FieldDirective,
  ChangeRequestSource.InternalDesignFeedback,
  ChangeRequestSource.FeeBuildProjectChange,
];

export function DetailsTab({ changeRequest, selectValues }: DetailsTabProps) {
  const { enums, regions, assets } = selectValues;
  const [saveChangeRequest, { loading: formSaving }] = useSaveChangeRequestMutation();
  const { openModal } = useModal();

  const scopeSelectValues = useMemo(() => getProjectTreeSelectValues(regions), [regions]);

  const assetSelectValues = useMemo(() => getAssetTreeSelectOptions(assets), [assets]);

  const formState = useFormState({
    config: formConfig,
    init: {
      input: changeRequest,
      map: (changeRequest) => ({
        ...changeRequest,
        source: changeRequest.source.code,
        scopes: changeRequest.scopes.map((s) => s.target.id),
        changeTypes: changeRequest.changeTypes,
        assets: changeRequest.changeRequestAssets.map((cra) => cra.id),
        estimateBudgetImpactInCents: changeRequest.estimateBudgetImpactInCents,
        markupBasisPoints: changeRequest.markupBasisPoints,
        estimatedBuyerOutOfPocketInCents: changeRequest.estimatedBuyerOutOfPocketInCents,
        estimatedRetailPriceInCents: changeRequest.estimatedRetailPriceInCents,
        estimateScheduleImpactInDays: changeRequest.estimateScheduleImpactInDays,
        priority: changeRequest.priority.code,
      }),
    },
    readOnly: READONLY_STATUSES.includes(changeRequest.status.code),
    autoSave: async ({ changedValue: { scopes, attachments, assets, ...rest } }) => {
      const updatedScope = mapFormInputToIncrementalOp(scopes, changeRequest.scopes, {
        existingValueToId: (crs) => crs.target.id,
        removedMapper: (removed) => ({ id: removed.id, op: IncrementalCollectionOp.Delete }),
        addedMapper: (added) => ({ targetId: added, op: IncrementalCollectionOp.Include }),
      });
      const updatedAsset = mapFormInputToIncrementalOp(assets, changeRequest.changeRequestAssets, {
        existingValueToId: (cra) => cra.id,
        removedMapper: (removed) => ({ id: removed.id, op: IncrementalCollectionOp.Remove }),
        addedMapper: (added) => ({ id: added, op: IncrementalCollectionOp.Include }),
      });

      await saveChangeRequest({
        variables: {
          input: {
            id: changeRequest.id,
            ...rest,
            ...(updatedAsset?.nonEmpty ? { changeRequestAssets: updatedAsset as SaveChangeRequestAsset[] } : {}),
            ...(updatedScope?.nonEmpty ? { scopes: updatedScope } : {}),
            attachments: attachments?.map(({ asset, ...props }) => ({
              ...props,
              asset: pick(asset, [
                // Dropping downloadUrl, attachmentUrl and createdAt to get the AssetInput shape
                "contentType",
                "fileName",
                "id",
                "s3Key",
                "sizeInBytes",
                "delete",
              ]),
            })),
          },
        },
        refetchQueries: ["ChangeLogRightPaneContentDetails"],
      });
    },
  });
  const hasReadOnlyMargin = useComputed(
    () => formState.source.value === ChangeRequestSource.CustomerRequestPersonalization,
    [formState.source.value],
  );

  // Temporally hide other sources until they are released
  const changeRequestSources = useMemo(
    () => filterChangeRequestSources(enums.changeRequestSource),
    [enums.changeRequestSource],
  );

  return (
    <PresentationProvider wrap>
      <Observer>
        {() => (
          <div css={Css.df.fdc.gap2.$}>
            <BoundTextAreaField label="Change Description" field={formState.rationale} />
            <div css={Css.df.fdc.gap1.$}>
              <span css={Css.gray600.sm.$}>Created {daysAgo(changeRequest.createdAt)} by</span>
              <Avatar src={changeRequest.createdBy.avatarUrl} size="sm" showName name={changeRequest.createdBy.name} />
            </div>
            <BoundSelectField
              label="Change Source (Where it came from)"
              field={formState.source}
              options={changeRequestSources}
              getOptionLabel={({ name }) => name}
              getOptionValue={({ code }) => code}
              onSelect={(value) => {
                if (!isPersonalizationChangeSource(value)) {
                  formState.isStandardOffering.set(undefined);
                  formState.salesforceOpportunityUrl.set(undefined);
                  formState.isUnderContract.set(undefined);
                  formState.productSelection.set(undefined);
                }
                formState.source.set(value);
                if (isPersonalizationChangeSource(value)) {
                  // tie specific hardcoded assets to the change type of personalization
                  formState.assets.set([
                    ...(formState.assets.value ?? []),
                    ...getRequiredChangeSourceAssets(assets, formState.source.value),
                  ]);
                }
              }}
            />
            <BoundTreeSelectField
              label="Projects / Developments Impacted"
              field={formState.scopes}
              options={scopeSelectValues}
              getOptionMenuLabel={getProjectsAndDevelopmentMenuLabel}
              onSelect={(options) => formState.scopes.set(options.leaf.values)}
              disabled={formSaving}
            />
            <BoundMultiSelectField
              label="Type of Change (What is affected)"
              required
              field={formState.changeTypes}
              options={enums.changeType}
              getOptionLabel={({ name }) => name}
              getOptionValue={({ code }) => code}
            />
            <BoundTreeSelectField
              label="Individual Assets (What is affected)"
              field={formState.assets}
              options={assetSelectValues}
              disabledOptions={getDisabledAssets(
                getRequiredChangeSourceAssets(assets, formState.source.value),
                formState.assets.value ?? [],
              )}
              defaultCollapsed
              // Use the `leaf` values so if a user selects an asset, store the asset selection (vs storing the parent business function)
              onSelect={(options) => formState.assets.set(options.leaf.values)}
              placeholder="--"
            />
            <BoundNumberField
              label="Estimated Budget Impact"
              type="cents"
              field={formState.estimateBudgetImpactInCents}
              helperText="This is the cost to Homebound for making the change."
            />
            <BoundNumberField
              label="Markup %"
              type="basisPoints"
              field={formState.markupBasisPoints}
              helperText="This is the expected markup for the buyer."
            />
            <BoundNumberField
              label="Estimated Buyer Out of Pocket/Revenue Impact"
              type="cents"
              field={formState.estimatedBuyerOutOfPocketInCents}
              helperText="This is the price the buyer will pay and should be reflected in the contract."
            />
            <BoundNumberField
              label="Estimated Retail Price"
              type="cents"
              field={formState.estimatedRetailPriceInCents}
              helperText="This is the value to the buyer if they were to pay full price for the change."
            />
            {hasReadOnlyMargin && !isDefined(formState.estimateMarginImpactBasisPoints.value) ? (
              <TextField
                label="Estimated Margin Impact %"
                value="N/A"
                helperText="This is the profit the change will generate."
                onChange={noop}
                readOnly
              />
            ) : (
              <BoundNumberField
                readOnly={formState.readOnly || hasReadOnlyMargin}
                label="Estimated Direct Margin Impact %"
                type="basisPoints"
                field={formState.estimateMarginImpactBasisPoints}
                helperText="This is the profit the change will generate."
              />
            )}
            <BoundNumberField
              label="Estimated Schedule Impact"
              type="days"
              field={formState.estimateScheduleImpactInDays}
            />
            <UrgencyField field={formState.priority} options={enums.changeRequestPriority} />
            <CustomerRequestPersonalizationInputs formState={formState} />
            <DependencyModal id={changeRequest.id} kind={ChangeRequestDependency.Block} />
            <DependencyModal id={changeRequest.id} kind={ChangeRequestDependency.Dependent} />
            <DependencyModal id={changeRequest.id} kind={ChangeRequestDependency.Link} />
            <BoundAttachments field={formState.attachments} />
            <div css={Css.df.jcsb.$}>
              <StaticField label={<div css={Css.truncate.mr2.$}>Last Updated</div>} labelStyle="left">
                <div css={Css.asb.$}>{daysAgo(changeRequest.updatedAt)}</div>
              </StaticField>
              <StaticField label={<div css={Css.mr2.$}>ID</div>} labelStyle="left">
                <div css={Css.asb.$}>{maybeDeTagId(changeRequest.id)}</div>
              </StaticField>
            </div>
            <div css={Css.df.aic.jcc.gap2.bt.bcGray200.py2.mbPx(-24).mxPx(-24).bgWhite.$}>
              <DuplicateButton changeRequestId={changeRequest.id} />
              {changeRequest.status.code !== ChangeRequestStatus.Denied && (
                <Button
                  label="Reject"
                  variant="danger"
                  icon="error"
                  onClick={() =>
                    openModal({
                      content: (
                        <ConfirmationModal
                          confirmationMessage="Are you sure you want to reject this change request? This will keep the change request, but close it so no further changes can be made. All future To-Dos and Approvals will be stopped, and all existing open ones will be cancelled."
                          label="Yes, Reject"
                          danger
                          onConfirmAction={async () =>
                            await saveChangeRequest({
                              variables: { input: { id: changeRequest.id, status: ChangeRequestStatus.Denied } },
                              refetchQueries: ["ChangeLogRightPaneContentDetails"],
                            })
                          }
                          title="Reject Request"
                        />
                      ),
                    })
                  }
                />
              )}
            </div>
          </div>
        )}
      </Observer>
    </PresentationProvider>
  );
}

function DuplicateButton({ changeRequestId }: { changeRequestId: string }) {
  const { openModal } = useModal();
  const { openInDrawer } = useSuperDrawer();

  const onConfirm = useCallback(
    () => openInDrawer({ content: <CreateChangeRequestDrawer copyViaChangeRequestId={changeRequestId} /> }),
    [openInDrawer, changeRequestId],
  );

  const onDuplicateClick = useCallback(
    () =>
      openModal({
        content: (
          <ConfirmationModal
            confirmationMessage="Are you sure you want to duplicate this change request? This will copy all of the fields to a new request, but start the To Dos and Approvals over from the beginning."
            label="Yes, Duplicate"
            onConfirmAction={onConfirm}
            title="Duplicate Request"
          />
        ),
      }),
    [openModal, onConfirm],
  );

  return <Button label="Duplicate" variant="tertiary" onClick={onDuplicateClick} />;
}

export type EditChangeRequestForm = Pick<
  SaveChangeRequestInput,
  | "rationale"
  | "estimateBudgetImpactInCents"
  | "markupBasisPoints"
  | "estimatedBuyerOutOfPocketInCents"
  | "estimatedRetailPriceInCents"
  | "estimateScheduleImpactInDays"
  | "estimateMarginImpactBasisPoints"
  | "isStandardOffering"
  | "salesforceOpportunityUrl"
  | "isUnderContract"
  | "productSelection"
> & {
  source: InputMaybe<ChangeRequestSource>;
  scopes: InputMaybe<string[]>;
  changeTypes: InputMaybe<ChangeType[]>;
  assets: InputMaybe<string[]>;
  priority: InputMaybe<ChangeRequestPriority>;
  attachments: InputMaybe<SaveAttachmentModel[]>;
};

const formConfig: ObjectConfig<EditChangeRequestForm> = {
  rationale: { type: "value", rules: [required] },
  source: { type: "value", rules: [required] },
  scopes: { type: "value", rules: [required, cannotBeEmpty] },
  changeTypes: { type: "value", rules: [required, cannotBeEmpty] },
  assets: { type: "value" },
  estimateBudgetImpactInCents: { type: "value" },
  markupBasisPoints: { type: "value" },
  estimatedBuyerOutOfPocketInCents: { type: "value" },
  estimatedRetailPriceInCents: { type: "value" },
  estimateScheduleImpactInDays: { type: "value" },
  estimateMarginImpactBasisPoints: { type: "value" },
  priority: { type: "value", rules: [required] },
  isStandardOffering: { type: "value" },
  salesforceOpportunityUrl: { type: "value" },
  isUnderContract: { type: "value" },
  attachments: { type: "list", config: attachmentConfig },
  productSelection: { type: "value" },
};

function getDisabledAssets(allDisabledAssets: string[], selectedAssetIds: string[]) {
  return allDisabledAssets.filter((asset) => selectedAssetIds.includes(asset));
}

export function filterChangeRequestSources(
  sources: ChangeLogRightPaneContentDetailsQuery["enumDetails"]["changeRequestSource"],
) {
  return sources.filter(({ code }) => ALLOWED_SOURCES.includes(code)).sortByKey("name");
}
